import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BuyNowDeliveryDetails, BuyNowOrder, DropdownOption } from "admin-common";
import { Product } from "../../../Models/product";
import { ProductConfiguration } from "../../../Models/product-configuration";
import { GuestOrderTO } from "../../../Models/guestOrderTO";
import { DeliveryAddress } from "../../../Models/DeliveryAddress";
import { OrderService } from "../../../Services/order.service";
import { DeliveryService } from "../../../Services/delivery.service";
import { AddressService } from "../../../Services/address.service";
import { AdressGlobalService } from "../../../shared/Services/adressGlobal.service";
import { WiSnackBarService } from "../../../Services/wi-snackBar.service";
import { MagasinService } from "../../../Services/magasin.service";
import { Magasin } from "../../../Models/magasin";

/** State passed to the order confirmation page */
export interface OrderConfirmationState {
  phoneNumber: string;
  isVirtual: boolean;
}

/**
 * "Acheter directement" page: lets a guest order the selected configuration
 * without an account (POST /new/orders/create/guest).
 */
@Component({
  selector: "app-buy-now-page",
  templateUrl: "./buy-now-page.component.html",
  standalone: false,
})
export class BuyNowPageComponent implements OnInit {
  /** Fallback image shown when the configuration has no image */
  readonly DEFAULT_PRODUCT_IMAGE = "assets/images/logo_placeholder.png";

  product?: Product;
  configuration: ProductConfiguration | null = null;
  quantity: number = 1;

  /** Disables the confirm button while the guest order is being created */
  loading: boolean = false;
  /** Relay points offered for the "POINT_DE_RELAIT" mode */
  relayPointsList: DropdownOption[] = [];
  /** Wilayas offered for the "DOMICILE" mode */
  wilayasList: DropdownOption[] = [];
  /** Communes of the selected wilaya */
  wilayaCities: DropdownOption[] = [];
  /** Stores offered for the "RECUPERATION_AU_NIVEAU_DU_MAGASIN" mode (GET /new/magasins) */
  stores: Magasin[] = [];
  /** Stores as dropdown options, the value is the store id */
  storesList: DropdownOption[] = [];
  /** Delivery price of the order, null until the delivery address is complete */
  deliveryPrice: number | null = null;

  /** Whether the configuration is virtual: the delivery section is then skipped */
  get isVirtual(): boolean {
    return this.configuration?.type === "VIRTUAL";
  }

  get productImgUrl(): string {
    return this.configuration?.images?.[0]?.url || this.DEFAULT_PRODUCT_IMAGE;
  }

  /** Price of the configuration x quantity, plus the delivery price, rounded up to the next hundred (1887.5 -> 1900) */
  get totalPrice(): number {
    const total =
      Number(this.configuration?.price ?? 0) * this.quantity +
      (this.deliveryPrice ?? 0);
    return Math.ceil(total / 100) * 100;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private deliveryService: DeliveryService,
    private addressService: AddressService,
    private adressGlobalService: AdressGlobalService,
    private wiSnackBarService: WiSnackBarService,
    private translate: TranslateService,
    private magasinService: MagasinService,
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe((data: any) => {
      this.product = data.detail;
      const configurationId = Number(
        this.route.snapshot.paramMap.get("configurationId"),
      );
      this.configuration =
        this.product?.configurations?.find((c) => c.id === configurationId) ??
        this.product?.configurations?.[0] ??
        null;

      // only active, purchasable configurations can be ordered
      if (
        !this.product?.activate ||
        !this.configuration ||
        ["RUPTURE_DE_STOCK", "SUR_COMMANDE", "OBSOLETE"].includes(
          this.configuration.state ?? "",
        )
      ) {
        this.goBack();
        return;
      }

      const requested = Number(this.route.snapshot.queryParamMap.get("quantity")) || 1;
      this.quantity = Math.max(
        1,
        Math.min(requested, this.configuration.quantity || requested),
      );
    });

    this.relayPointsList = this.adressGlobalService.relayPointsList.map((point) =>
      this.adressGlobalService.getRelayPointOption(point),
    );
    this.wilayasList = this.adressGlobalService.getWilayas().map((wilaya) => ({
      label: this.adressGlobalService.getWilayaLabel(wilaya),
      value: wilaya,
    }));
    this.getStores();
  }

  /**
   * Loads the stores offered for store pickup.
   * @method getStores
   */
  getStores(): void {
    this.magasinService.getAll().subscribe({
      next: (stores) => {
        this.stores = stores;
        this.storesList = this.magasinService.toStoreOptions(stores);
      },
      error: () => {
        this.stores = [];
        this.storesList = [];
      },
    });
  }

  /**
   * Navigates back to the product detail page.
   * @method goBack
   */
  goBack(): void {
    const { name, id, configurationId } = this.route.snapshot.params;
    this.router.navigate(["/products", name, id, configurationId]);
  }

  /**
   * Loads the communes of the selected wilaya.
   * @method onWilayaSelected
   */
  onWilayaSelected(wilaya: any): void {
    this.wilayaCities = [];
    if (!wilaya?.idWilaya) return;
    this.addressService.getTownsByWilayaId(Number(wilaya.idWilaya)).subscribe({
      next: (towns) => {
        this.wilayaCities = towns.map((town) => ({
          label: town.label || town.name,
          value: town.name,
        }));
      },
      error: () => {
        this.wilayaCities = [];
      },
    });
  }

  /**
   * Computes the delivery price once the delivery address is complete.
   * @method onDeliveryDetails
   */
  onDeliveryDetails(details: BuyNowDeliveryDetails): void {
    const address = this.buildGuestDeliveryAddress(details);
    if (!address.wilaya || (details.deliveryType === "DOMICILE" && !address.ville)) {
      this.deliveryPrice = null;
      return;
    }
    this.deliveryService
      .getDeliveryPriceYalidine(address.wilaya, address.ville, details.deliveryType)
      .subscribe({
        next: (price) => (this.deliveryPrice = price ?? 0),
        error: () => (this.deliveryPrice = null),
      });
  }

  /**
   * Creates the guest order, then opens the order confirmation page.
   * @method onConfirmOrder
   */
  onConfirmOrder(order: BuyNowOrder): void {
    if (!this.configuration?.id || this.loading) return;

    const deliveryType = this.isVirtual ? "NO_DELIVERY" : order.deliveryType!;
    const email = order.email?.trim() || undefined;
    const guestOrder: GuestOrderTO = {
      cartProductTO: {
        quantity: this.quantity,
        configurationId: this.configuration.id,
      },
      deliveryAddressTO: {
        ...this.buildGuestDeliveryAddress({ ...order, deliveryType } as BuyNowDeliveryDetails),
        firstname: order.firstName,
        lastname: order.lastName,
        phone: order.phoneNumber,
        email,
      },
      deliveryType,
      livreurId: 1,
      recoveryMode: null,
      // virtual products must be paid in advance, physical ones are paid on delivery
      paymentMethod: this.isVirtual ? "VIREMENT" : "CASH_EN_DELIVERY",
      email,
      magasinId:
        deliveryType === "RECUPERATION_AU_NIVEAU_DU_MAGASIN"
          ? order.storeId
          : undefined,
    };

    this.loading = true;
    this.orderService.directOrder(guestOrder).subscribe({
      next: (orderId) => {
        this.loading = false;
        const state: OrderConfirmationState = {
          phoneNumber: order.phoneNumber,
          isVirtual: this.isVirtual,
        };
        this.router.navigate(["/products/order-confirmation", orderId], { state });
      },
      error: () => {
        this.loading = false;
        this.wiSnackBarService.openSnackBar(
          this.translate.instant("productsPage.snackbar.orderCreationError"),
          this.translate.instant("services.snackbar.close"),
          "error-snackbar",
        );
      },
    });
  }

  /**
   * Builds the guest order's delivery address (without contact details) from the delivery mode.
   * @method buildGuestDeliveryAddress
   */
  private buildGuestDeliveryAddress(
    details: BuyNowDeliveryDetails & { address?: string },
  ): DeliveryAddress {
    const address: DeliveryAddress = {
      firstname: "",
      lastname: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      wilaya: "SETIF",
      ville: "Sétif",
      postalCode: null,
    };
    switch (details.deliveryType) {
      case "POINT_DE_RELAIT": {
        // same as the connected cart: no commune, so the delivery is priced on the wilaya's chef-lieu
        // (the option value shows the point in the current language: the order keeps the French texts)
        const relayPoint = this.adressGlobalService.toOriginalRelayPoint(details.relayPoint);
        return {
          ...address,
          addressLine1: relayPoint?.address?.[0] ?? "",
          wilaya: relayPoint?.value?.replace(/[0-9]/g, "") ?? "",
          ville: "",
        };
      }
      case "DOMICILE":
        return {
          ...address,
          addressLine1: details.address ?? "",
          wilaya: details.wilaya?.value ?? "",
          ville: details.city ?? "",
        };
      case "RECUPERATION_AU_NIVEAU_DU_MAGASIN": {
        // the delivery address is the selected store's address
        const store = this.stores.find((item) => item.id === details.storeId);
        return {
          ...address,
          addressLine1: store?.address || store?.name || "",
          wilaya: store?.wilaya ?? "",
          ville: store ? this.adressGlobalService.getWilayaName(store.wilaya) : "",
        };
      }
      default:
        // virtual products are delivered electronically
        return address;
    }
  }
}
